import { emsClient } from '../api/clients';
import type { EmsComponents } from '../api/ems';
import type { GeoPoint, LocationSource } from '../realtime';

export type AvailableCollaborationAgency =
  Omit<EmsComponents['schemas']['GetPortalAvailableAgenciesResponse']['agencies'][number], 'distanceFromIncidentInMeters'> & {
    distanceFromIncidentInMeters: number | null;
  };

function ensureSuccess(response: Response, action: string): void {
  if (!response.ok) {
    throw new Error(`${action} failed (HTTP ${response.status})`);
  }
}

export async function closeIncident(incidentId: string): Promise<void> {
  const { response } = await emsClient().POST('/v1/incidents/{incidentId}/close', {
    params: { path: { incidentId } },
  });
  ensureSuccess(response, 'Close incident');
}

export async function locateCaller(incidentId: string): Promise<void> {
  const { response } = await emsClient().POST('/v1/incidents/{incidentId}/caller/locate', {
    params: { path: { incidentId } },
  });
  ensureSuccess(response, 'Locate caller');
}

export async function activateChat(incidentId: string): Promise<void> {
  const { response } = await emsClient().POST('/v1/incidents/{incidentId}/caller/chat/activate', {
    params: { path: { incidentId } },
  });
  ensureSuccess(response, 'Activate caller chat');
}

export async function confirmCollaboration(incidentId: string): Promise<void> {
  const { response } = await emsClient().POST('/v1/incidents/{incidentId}/collaboration/accept', {
    params: { path: { incidentId } },
  });
  ensureSuccess(response, 'Confirm collaboration');
}

export async function rejectCollaboration(incidentId: string): Promise<void> {
  const { response } = await emsClient().POST('/v1/incidents/{incidentId}/collaboration/decline', {
    params: { path: { incidentId } },
  });
  ensureSuccess(response, 'Reject collaboration');
}

export async function requestCollaboration(incidentId: string, agencyId: string): Promise<void> {
  const { response } = await emsClient().POST('/v1/incidents/{incidentId}/collaboration/invite', {
    params: { path: { incidentId } },
    body: { agencyId },
  });
  ensureSuccess(response, 'Request collaboration');
}

export async function getPortalAvailableCollaborationAgencies(
  incidentId: string,
): Promise<AvailableCollaborationAgency[]> {
  const { data, response } = await emsClient().GET(
    '/portal/v1/incident/{id}/collaboration/available-agencies',
    {
      params: { path: { id: incidentId } },
    },
  );
  ensureSuccess(response, 'Get available collaboration agencies');

  if (data === undefined) {
    throw new Error('Get available collaboration agencies returned no data.');
  }

  return data.agencies.map((agency) => ({
    ...agency,
    distanceFromIncidentInMeters: agency.distanceFromIncidentInMeters ?? null,
  }));
}

export async function manualPositionUpdate(
  incidentId: string,
  gpsCoordinates: GeoPoint,
  capturedAtUtc: string = new Date().toISOString(),
): Promise<void> {
  const { response } = await emsClient().POST('/v1/incidents/{incidentId}/caller/track', {
    params: { path: { incidentId } },
    body: { location: { ...gpsCoordinates, capturedAtUtc, source: 'Entered' } },
  });
  ensureSuccess(response, 'Manual position update');
}

export async function changeCallerLocationFromMap(incidentId: string, coordinates: GeoPoint): Promise<void> {
  await manualPositionUpdate(incidentId, coordinates);
}

export async function changePreferredCallerLocationSource(
  incidentId: string,
  preferredLocationSource: LocationSource,
): Promise<void> {
  const { response } = await emsClient().POST('/portal/v1/incident/{id}/preferred-caller-location-source', {
    params: { path: { id: incidentId } },
    body: { preferredLocationSource },
  });
  ensureSuccess(response, 'Change preferred caller location source');
}

export async function updateCallerName(incidentId: string, name: string): Promise<void> {
  const { response } = await emsClient().PATCH('/v1/incidents/{incidentId}/caller/full-name', {
    params: { path: { incidentId } },
    body: { fullName: name },
  });
  ensureSuccess(response, 'Update caller name');
}

export async function updateCallerPhoneNumber(incidentId: string, phoneNumber: string): Promise<void> {
  const { response } = await emsClient().POST('/portal/v1/incident/{id}/caller-phone-number', {
    params: { path: { id: incidentId } },
    body: { phoneNumber },
  });
  ensureSuccess(response, 'Update caller phone number');
}

export async function changeDispatcherNote(incidentId: string, note: string): Promise<void> {
  const { response } = await emsClient().PATCH('/v1/incidents/{incidentId}/summary', {
    params: { path: { incidentId } },
    body: { summary: note },
  });
  ensureSuccess(response, 'Change dispatcher note');
}

export async function changePatientDescription(incidentId: string, description: string): Promise<void> {
  const { response } = await emsClient().PATCH('/v1/incidents/{incidentId}/patient/description', {
    params: { path: { incidentId } },
    body: { description },
  });
  ensureSuccess(response, 'Change patient description');
}

export async function changeCallSign(incidentId: string, callSign: string): Promise<void> {
  const { response } = await emsClient().PATCH('/v1/incidents/{incidentId}/call-sign', {
    params: { path: { incidentId } },
    body: { callSign },
  });
  ensureSuccess(response, 'Change call sign');
}

export async function changeIncidentLabel(incidentId: string, incidentLabel: string): Promise<void> {
  const { response } = await emsClient().PATCH('/v1/incidents/{incidentId}/label', {
    params: { path: { incidentId } },
    body: { label: incidentLabel },
  });
  ensureSuccess(response, 'Change incident label');
}

export async function sendSmsMessage(phoneNumber: string, text: string): Promise<void> {
  const { response } = await emsClient().POST('/portal/v1/send-sms', {
    body: { phoneNumber, text },
  });
  ensureSuccess(response, 'Send SMS message');
}
